import {
  Bot,
  BrushIcon,
  RocketIcon,
  GlobeIcon,
  Goal,
  MessagesSquareIcon,
  Search,
  BookOpen,
  BarChart3,
  ChartNoAxesColumnIncreasing,
} from "lucide-react";

// External apps icons
import CIPIcon from "@shared-assets/cip.png";
import ISearchIcon from "@shared-assets/isearch.png";
import AiSummitThumbnail from "@shared-assets/tutorial-videos/ai-summit-2025.jpg";
import CreateAiProfilesThumbnail from "@shared-assets/tutorial-videos/create-ai-profiles.jpg";
import WebSearchThumbnail from "@shared-assets/tutorial-videos/web-search.jpeg";
import DocumentGeneratorThumbnail from "@shared-assets/tutorial-videos/document-generator.jpeg";
import type { StaticImport } from "next/dist/shared/lib/get-img-props";
import type { ReactNode } from "react";
import {
  BETA_INCLUDED_FEATURES,
  BETA_FLAG,
  type FeatureFlagType,
} from "@shared-utils/constants";

type ExternalApp = {
  name: string;
  url: string;
  image: string | StaticImport;
};

export type HelpLink = {
  key: "academy" | "faq";
  label: string;
  href: string;
};

export type Update = {
  date: string;
  key: string;
  title: string;
  url: string;
  body: ReactNode;
};

export type TutorialVideo = {
  title: string;
  url: string;
  thumbnail: string | StaticImport;
};

type Metadata = {
  aiTools: AITool[];
  tutorialVideos: TutorialVideo[];
  externalApps: ExternalApp[];
  helpLinks: Record<HelpLink["key"], HelpLink>;
};

export type AITool = {
  slug:
    | "chat-ai"
    | "agent-ai"
    | "research-ai"
    | "market-research"
    | "training-ai"
    | "translate-ai"
    | "image-ai"
    | "external-ai"
    | "metrics-ai"
    | "isearch"
    | "docs"
    | "/";
  tool: boolean;
  featured?: boolean;
  adminOnly?: boolean;
  requiredFeatureFlags?: FeatureFlagType[];
  /** Lets platform admins discover this tool without its flags; grants no workspace access. */
  adminBypassFeatureFlags?: boolean;
  /** Includes the tool in Agent AI's instance tool-access selector. */
  instanceConnector?: {
    enabled: boolean;
  };
  meta: {
    fillColor?: string;
    strokeColor?: string;
    backgroundColor?: string;
    backgroundColorHover?: string;
    name: string;
    description: string;
    byline: string;
    contactName: string;
    contactEmail: string;
  };
};

export const getIconFromSlug = (
  slug: AITool["slug"],
  props?: {
    size?: number;
    strokeWidth?: number;
    className?: string;
  },
) => {
  switch (slug) {
    case "chat-ai":
      return <MessagesSquareIcon size={18} strokeWidth={0.75} {...props} />;
    case "agent-ai":
      return <Bot size={18} strokeWidth={0.75} {...props} />;
    case "translate-ai":
      return <GlobeIcon size={18} strokeWidth={0.75} {...props} />;
    case "image-ai":
      return <BrushIcon size={18} strokeWidth={0.75} {...props} />;
    case "external-ai":
      return <RocketIcon size={18} strokeWidth={0.75} {...props} />;
    case "training-ai":
      return <Goal size={18} strokeWidth={0.75} {...props} />;
    case "isearch":
      return <Search size={18} strokeWidth={0.75} {...props} />;
    case "research-ai":
      return <BookOpen size={18} strokeWidth={0.75} {...props} />;
    case "market-research":
      return (
        <ChartNoAxesColumnIncreasing size={18} strokeWidth={0.75} {...props} />
      );
    case "metrics-ai":
      return <BarChart3 size={18} strokeWidth={0.75} {...props} />;
  }
};

export const metadata: Metadata = {
  aiTools: [
    {
      slug: "chat-ai",
      tool: true,
      featured: true,
      meta: {
        fillColor: "fill-ter-blue-500",
        strokeColor: "stroke-ter-blue-400",
        backgroundColor: "bg-ter-blue-500",
        backgroundColorHover: "hover:bg-ter-blue-200",
        name: "LEO GPT",
        description:
          "Ask questions, request help on specific tasks, get creative, or upload a document to “chat with”.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "agent-ai",
      tool: true,
      adminOnly: true,
      requiredFeatureFlags: ["AGENT_AI__APP__219103"],
      meta: {
        fillColor: "fill-sec-black",
        strokeColor: "stroke-sec-black",
        backgroundColor: "bg-sec-black",
        backgroundColorHover: "hover:bg-panel-300",
        name: "Agent AI",
        description: "Workspace-scoped tool scaffolding for Agent AI.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "isearch",
      requiredFeatureFlags: [],
      tool: true,
      meta: {
        fillColor: "fill-ter-cyan-500",
        strokeColor: "stroke-ter-cyan-500",
        backgroundColor: "bg-ter-cyan-500",
        backgroundColorHover: "hover:bg-ter-cyan-200",
        name: "iSearch",
        description:
          "An intelligent platform to help you manage & search complex databases.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "research-ai",
      tool: true,
      requiredFeatureFlags: ["RESEARCH_AI__APP__205402"],
      meta: {
        fillColor: "fill-ter-bluegray-400",
        strokeColor: "stroke-ter-bluegray-500",
        backgroundColor: "bg-ter-bluegray-500",
        backgroundColorHover: "hover:bg-ter-bluegray-200",
        name: "Research AI",
        description:
          "Conduct deep research tasks by scraping online information.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "market-research",
      tool: true,
      requiredFeatureFlags: ["MARKET_RESEARCH__APP__225996"],
      adminBypassFeatureFlags: true,
      instanceConnector: { enabled: true },
      meta: {
        fillColor: "fill-ter-bluegray-400",
        strokeColor: "stroke-ter-bluegray-500",
        backgroundColor: "bg-ter-bluegray-500",
        backgroundColorHover: "hover:bg-ter-bluegray-200",
        name: "Market Research",
        description: "Create and manage market research analyses.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "training-ai",
      requiredFeatureFlags: ["TRAINING_AI__APP__189322"],
      tool: true,
      meta: {
        fillColor: "fill-ter-mint-500",
        strokeColor: "stroke-ter-mint-500",
        backgroundColor: "bg-ter-mint-500",
        backgroundColorHover: "hover:bg-ter-mint-200",
        name: "Role-Play AI",
        description:
          "Role-Play AI is a tool that helps you to train a machine learning model to predict the probability of a patient having a specific disease.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "translate-ai",
      tool: true,
      meta: {
        fillColor: "fill-ter-purple-500",
        strokeColor: "stroke-ter-purple-500",
        backgroundColor: "bg-ter-purple-500",
        backgroundColorHover: "hover:bg-ter-purple-200",
        name: "Translate AI",
        description:
          "Use the power of AI to translate short content or an entire document - while preserving its formatting. ",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "image-ai",
      tool: true,
      meta: {
        fillColor: "fill-ter-green-500",
        strokeColor: "stroke-ter-green-500",
        backgroundColor: "bg-ter-green-500",
        backgroundColorHover: "hover:bg-ter-green-200",
        name: "Image GenAI",
        description: "Transform written prompts into powerful visuals. ",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "external-ai",
      requiredFeatureFlags: ["EXTERNAL_AI__223473"],
      tool: true,
      meta: {
        fillColor: "fill-ter-blue-500",
        strokeColor: "stroke-ter-blue-500",
        backgroundColor: "bg-ter-blue-500",
        backgroundColorHover: "hover:bg-ter-blue-200",
        name: "External AI",
        description: "Work with AI experiences outside LEO AI.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "metrics-ai",
      requiredFeatureFlags: ["METRICS_AI__APP__223356"],
      tool: true,
      meta: {
        fillColor: "fill-zinc-500",
        strokeColor: "stroke-zinc-500",
        backgroundColor: "bg-zinc-500",
        backgroundColorHover: "hover:bg-zinc-200",
        name: "Metrics",
        description: "View and analyze AI usage metrics and insights.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "isearch",
      tool: false,
      meta: {
        fillColor: "fill-ter-green-500",
        strokeColor: "stroke-ter-green-500",
        backgroundColor: "bg-ter-green-500",
        backgroundColorHover: "hover:bg-ter-green-400",
        name: "iSearch",
        description:
          "iSearch is an intelligent platform supported by machine learning algorithms to help you manage complex databases easier and faster. The platform is best suited for heavy text databases and tables with multiple text columns.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "docs",
      tool: false,
      meta: {
        name: "Docs",
        description: "Documentation for the AI & Cloud Data Services team",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
  ],
  tutorialVideos: [
    {
      title: "AI Summit 2025",
      url: "https://leopharma.sharepoint.com/:v:/t/LEO-PHARMA-ACADEMY/IQAVWLXoBBVJRYgcejI1Bhr-AWnbRcAY3aCUvZo8sIoMfBE?e=MxeWDl&nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJTdHJlYW1XZWJBcHAiLCJyZWZlcnJhbFZpZXciOiJTaGFyZURpYWxvZy1MaW5rIiwicmVmZXJyYWxBcHBQbGF0Zm9ybSI6IldlYiIsInJlZmVycmFsTW9kZSI6InZpZXcifX0%3D",
      thumbnail: AiSummitThumbnail,
    },
    {
      title: "Creating AI Profiles",
      url: "https://leopharma.sharepoint.com/:v:/s/ONELEOKOLuncertaintymatrix/IQAfkc0rU4VlS7lOn_owlP1wAUirM6kcOeExJAUUuXWpKQ8?e=5R2Xhd&nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJTdHJlYW1XZWJBcHAiLCJyZWZlcnJhbFZpZXciOiJTaGFyZURpYWxvZy1MaW5rIiwicmVmZXJyYWxBcHBQbGF0Zm9ybSI6IldlYiIsInJlZmVycmFsTW9kZSI6InZpZXcifX0%3D",
      thumbnail: CreateAiProfilesThumbnail,
    },
    {
      title: "Web Search",
      url: "https://leopharma.sharepoint.com/:v:/s/ONELEOKOLuncertaintymatrix/IQAInln4Cs8LTIFGTN2dfPaJAQhwvLw7JU_aY7JNBclmUM4?e=I21Mx9&nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJTdHJlYW1XZWJBcHAiLCJyZWZlcnJhbFZpZXciOiJTaGFyZURpYWxvZy1MaW5rIiwicmVmZXJyYWxBcHBQbGF0Zm9ybSI6IldlYiIsInJlZmVycmFsTW9kZSI6InZpZXcifX0%3D",
      thumbnail: WebSearchThumbnail,
    },
    {
      title: "Document generator",
      url: "https://leopharma.sharepoint.com/:v:/s/ONELEOKOLuncertaintymatrix/IQDNTosWbdGZQbbUarNk_27UAc7FRpKwNnfKiwGuDOvI_YA?e=fOfzjU&nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJTdHJlYW1XZWJBcHAiLCJyZWZlcnJhbFZpZXciOiJTaGFyZURpYWxvZy1MaW5rIiwicmVmZXJyYWxBcHBQbGF0Zm9ybSI6IldlYiIsInJlZmVycmFsTW9kZSI6InZpZXcifX0%3D",
      thumbnail: DocumentGeneratorThumbnail,
    },
  ],
  externalApps: [
    {
      url: "https://isearch.leopharma-app.com/",
      name: "iSearch",
      image: ISearchIcon,
    },
    {
      url: "https://cip.leopharma-app.com/",
      name: "Competitor Information Platform",
      image: CIPIcon,
    },
  ],
  helpLinks: {
    academy: {
      key: "academy",
      label: "LEO Academy",
      href: "https://leopharma.sharepoint.com/teams/LEO-PHARMA-ACADEMY/SitePages/LEO-OpenAI.aspx",
    },
    faq: {
      key: "faq",
      label: "FAQ",
      href: "https://leopharma.sharepoint.com/:b:/t/LEO-PHARMA-ACADEMY/IQCiNA6MF77pR7TpT35dl65RAbc6wANYers4sPvoBIK0YoE?e=5cfXua",
    },
  },
};

export const slugToTool = metadata.aiTools.reduce((map, t) => {
  map.set(t.slug, t);
  return map;
}, new Map<string, AITool>());

/**
 * Applies catalog role and feature-flag policy consistently across dashboard and menu.
 * Only tools explicitly opting in bypass flags for platform admins; visibility grants no access.
 * @param aiTool - Tool metadata declaring its discovery policy.
 * @param activeFeatureFlags - Loaded active flags, or undefined while flags are unavailable.
 * @param isAdmin - Whether the current user is a platform admin.
 */
export const isToolVisible = (
  aiTool: AITool,
  activeFeatureFlags: FeatureFlagType[] | undefined,
  isAdmin: boolean,
) => {
  if (aiTool.adminOnly && !isAdmin) {
    return false;
  }

  if (isAdmin && aiTool.adminBypassFeatureFlags) {
    return true;
  }

  if (!aiTool.requiredFeatureFlags) {
    return true;
  }

  const hasExplicitFlags = aiTool.requiredFeatureFlags.every((flag) =>
    activeFeatureFlags?.includes(flag),
  );

  // Beta only grants access to the features it explicitly covers
  const isBetaIncluded = aiTool.requiredFeatureFlags.every((flag) =>
    BETA_INCLUDED_FEATURES.includes(flag),
  );
  if (!isBetaIncluded) {
    return hasExplicitFlags;
  }

  return activeFeatureFlags?.includes(BETA_FLAG) || hasExplicitFlags;
};
